<template>
  <div class="hcl-form">
    <div class="hcl-form-wrapper">
      <div
        v-if="formConfigurationModeEditor"
        class="hcl-form-tools"
      >
        <div class="hcl-form-tools-group">
          <div
            class="toggle"
            @click="formToolsEditModeActiveToggle"
          >
            <i
              v-if="formToolsEditModeActive"
              class="toggle-icon eii--fa6-solid--eye"
            ></i>
            <i
              v-if="!formToolsEditModeActive"
              class="toggle-icon eii--fa6-solid--pencil"
            ></i>
          </div>
          <div class="count-total">{{ tiles.count.total }}</div>
          <div class="count-form">{{ tiles.count.form }}</div>
          <div class="count-chooser">{{ tiles.count.chooser }}</div>
        </div>
      </div>
      <form
        ref="formRef"
        :id="formName"
        :name="formName"
        :action="formAction"
        method="post"
      >
        <template
          v-for="field in props.data.hidden"
          :key="field.name"
        >
          <input
            type="text"
            :name="field.name"
            :value="field.value"
            hidden
          />
        </template>
        <hcl-tiles-editor
          v-if="formEditMode"
          :tiles="tiles"
        />
        <hcl-tiles-renderer
          v-if="!formEditMode"
          :tiles="tiles"
          v-on:hcl-tiles-tile-change-height="onTilesTileChangeHeight"
          v-on:hcl-form-input-enter-key="onFormInputEnterKey"
        />
      </form>
      <div class="hcl-form-chooser">
        <hcl-form-fields-selector :config="props.definition.config"></hcl-form-fields-selector>
      </div>
    </div>
    <div
      class="hcl-form-footer"
      v-if="componentsToolbars(props.definition.footer).length > 0"
    >
      <component
        v-for="footer in props.definition.footer"
        :is="componentMeta(footer.meta)"
        :key="footer.meta.id"
        :definition="footer"
        v-on:hcl-button-clicked="onButtonClicked"
      >
      </component>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, watch, inject, provide } from 'vue'

import { useMeta } from '@/composables/meta'
import { useIO } from '@/composables/io'
import type { EventGlobalEventBus } from '@/composables/global-event-bus'
import { useGlobalEventBus } from '@/composables/global-event-bus'

import { useStateStore } from '@/stores/state'

import HclTilesEditor from '@/components/TilesEditor/TilesEditor.vue'
import HclTilesRenderer from '@/components/TilesRenderer/TilesRenderer.vue'
import HclFormFieldsSelector from "@/components/FormFieldsSelector/FormFieldsSelector.vue"

import { DefinitionForm, DefinitionToolbar, DataForm, EventButtonClicked } from '@/components/types'

import { DefinitionTilesOptions } from '@/components/types'

import { useTiles } from '@/components/TilesEditor/tiles'

import { serialize } from './serialize'

interface FormPostData {
  source: { id: string },
  values: object
}

interface Props {
  /**
   * Definition as an JS object.
   */
  definition: DefinitionForm
  /**
   * Data as an JS object.
   */
  data: DataForm
  /**
   * Form Name
   */
  name: string
}

const props = withDefaults(defineProps<Props>(), {
  definition: undefined
})

const { componentMeta } = useMeta()

const blockMetaId: string = inject('blockMetaId', '')
const blockContentMetaId: string = inject('blockContentMetaId', '')

const componentsToolbars = (toolbars: DefinitionToolbar[] | undefined): DefinitionToolbar[] => {
  if (toolbars === undefined) return []
  return toolbars
}

provide('formName', props.name)
provide('definitionConfig', props.definition.config)
provide('definitionFields', props.definition.fields)
provide('dataFields', props.data.fields)
provide('dataLookups', props.data.lookups)

/*
  * Form & Form Tools
  */
const formRef = ref()

const formName = props.definition.config.name ?? ''
const formAction = props.definition.config.action ?? ''
const formConfigurationModeEditor = props.definition.config.mode === 'editor'
const formConfigurationEditorActive = props.definition.config.editor === true

const formToolsEditModeActive = ref(
  formConfigurationModeEditor && formConfigurationEditorActive
)

const formEditMode = formConfigurationModeEditor && formToolsEditModeActive

const formToolsEditModeActiveToggle = () => {
  return formToolsEditModeActive.value = !formToolsEditModeActive.value
}

/*
  * Tiles
  */
const options: DefinitionTilesOptions = reactive({
  dimensions: {
    rows: {
      count: 5,
      height: 75,
      unit: 'px'
    },
    cols: {
      count: 12,
      width: undefined,
      unit: undefined
    }
  }
})

const { tiles, generateTilesFromFormFields } = useTiles(
  options,
  props.definition.config.type,
  'FormElement',
  props.definition.fields
)

generateTilesFromFormFields()

const onTilesTileChangeHeight = (event) => {
  const match = tiles.tiles.filter((tile) => {
    return tile.id === event.tileId
  })

  if (match.length === 0) return

  match[0].height = event.tileHeight
}

const nullifyFieldsChooserValue = () => {
  for (const field of props.definition.fields) {
    if (field.layout.chooser) {
      for (const d of props.data.fields) {
        if (d.name === field.field.name) {
          d.value = ''
        }
      }
    }
  }
}

watch(props.definition, () => {
  generateTilesFromFormFields()
  nullifyFieldsChooserValue()
})

/*
 * Event Handling
 */
const { performPostBucket } = useIO()

const addDynamicInputValues = (data: FormPostData) => {
  if (!formRef.value) return

  for (const field of props.definition.fields) {
    if (field.field.type === 'widget' || field.field.type === 'date') {
      const groupName = field.field?.group?.name ?? undefined
      if (groupName) {
        const fullFieldName = field.field.form + '[' + groupName + ']' + '[' + field.field.name + ']'
        for (const element of formRef.value.elements) {
          if (element.name === fullFieldName) {
            data.values[groupName][field.field.name] = element.value
          }
        }
      } else {
        const fullFieldName = field.field.form + '[' + field.field.name + ']'
        for (const element of formRef.value.elements) {
          if (element.name === fullFieldName) {
            data.values[field.field.name] = element.value
          }
        }
      }
    }
  }
}

const addFreeFormHtmlInputValues = (data: FormPostData, fieldsFormName: string | undefined) => {
  if (!formRef.value) return
  if (fieldsFormName === undefined) return

  /*
    * All form fields with type 'html' are assumed to be free form,
    * i.e. they are not rendered via vue, and thus we do not know
    * which values have been entered in the input fields.
    * We need to use normal javascript to retrieve the form
    * and the associated input fields.
    */
  const formSelector = 'form[id="' + props.definition.config.name + '"]'
  const form = document.querySelector(formSelector)

  if (form === null) return

  /*
    * Sadly using `new FormData(form)` does not work, so we use
    * a alternative serialization library, which only returns
    * the input fields from form fields with type 'html'.
    */
  const formDataHtml = serialize(form as HTMLFormElement, { hash: true })

  const dataToAdd = formDataHtml[fieldsFormName]

  for (const key in dataToAdd) {
    if (!(key in data.values)) {
      data.values[key] = dataToAdd[key]
    }
  }
}

const submitFormAjax = () => {
  const namesVisibleFields: string[] = []

  let fieldsFormName: string | undefined = undefined

  for (const field of props.definition.fields) {
    if (fieldsFormName === undefined) {
      fieldsFormName = field.field.form
    }
    if (!field.chooser.selectable) {
      namesVisibleFields.push(field.field.name)
    }
    if (field.chooser.selectable && field.chooser.selected) {
      namesVisibleFields.push(field.field.name)
    }
  }

  const values = {}

  if (props.data.fields) {
    for (const nameVisibleField of namesVisibleFields) {
      for (const dataField of props.data.fields) {
        if (dataField.name === nameVisibleField) {
          if (dataField.group) {
            if (!(dataField.group in values)) values[dataField.group] = {}
            values[dataField.group][dataField.name] = dataField.value
          } else {
            values[dataField.name] = dataField.value
          }
        }
      }
    }
  }

  if (props.data?.hidden) {
    for (let field of props.data.hidden) {
      values[field.name] = field.value
    }
  }

  const data: FormPostData = {
    source: {
      id: blockContentMetaId
    },
    values: values
  }

  addDynamicInputValues(data)
  addFreeFormHtmlInputValues(data, fieldsFormName)

  performPostBucket(
    props.definition.config.action,
    {},
    data,
    blockMetaId,
    (data) => { }
  )
}

const emit = defineEmits(['hcl-button-clicked'])

const onButtonClicked = (event: EventButtonClicked) => {
  emit('hcl-button-clicked', event)

  if (event.type === 'submit-ajax') {
    submitFormAjax()
  }
}

const { getBlockGroupNameValue, updateBlockGroupNameValue } = useStateStore()

const doSearch = getBlockGroupNameValue(blockMetaId, 'global', 'search')

watch(doSearch, (n: boolean) => {
  if (n === true) {
    updateBlockGroupNameValue(blockMetaId, 'global', 'search', false)
    submitFormAjax()
  }
})

if ('autosearch' in props.definition.config && props.definition.config.autosearch) {
  submitFormAjax()
}

const onFormInputEnterKey = () => {
  submitFormAjax()
}

const { eventBus } = useGlobalEventBus()

const listener = (event: EventGlobalEventBus) => {
  if (event.sourceType === 'favorites') {
    submitFormAjax()
  }
}

eventBus.on(listener)
</script>

<style lang="scss" scoped>
.hcl-form {
  padding: 1rem;
  background-color: rgb(240, 240, 240);

  .hcl-form-wrapper {
    display: flex;
    flex-direction: row;

    .hcl-form-tools {
      width: 3rem;
      justify-content: start;
    }

    form {
      flex: 1 0;
    }

    .hcl-form-chooser {
      display: flex;
      width: 3rem;
      justify-content: end;
    }
  }
}

// .hcl-form {

//   .hcl-form-wrapper {
//     display: flex;
//     flex: 1 0 auto;

//     &.form-wrapper-spacing-top {
//       margin-top: 1rem
//     }

//     &.form-wrapper-spacing-bottom {
//       margin-bottom: 1rem
//     }

//     form {
//       width: 100%;
//     }

//     .hcl-form-tools {
//       width: 2.25rem;

//       .hcl-form-tools-group {
//         position: sticky;
//         top: 0.25rem;
//         display: flex;
//         flex-direction: column;
//         border-radius: 0.25rem;
//         color: white;
//         padding: 0.5rem 0;
//         line-height: 1;
//         align-items: center;
//         margin: 0 3px;
//         background-color: var(--gray-dark);
//         opacity: 0.6;

//         &:hover {
//           opacity: 1;
//         }

//         .toggle {
//           margin-left: auto;
//           margin-right: auto;
//           padding-bottom: 1rem;
//           cursor: pointer;

//           .toggle-icon {
//             font-size: 1.1rem;
//           }
//         }

//         .count-total {
//           padding-bottom: 1rem;
//           font-weight: 500;
//         }

//         .count-form {
//           padding-bottom: 1rem;
//           font-weight: 500;
//         }

//         .count-chooser {
//           font-weight: 500;
//         }
//       }
//     }
//   }

// }
</style>
